<template>
  <div>
    <container
      :isModal="true"
      @close-modal="closeScreen()"
      :loading="stateLoading || !stateReady"
    >
      <template v-slot:header>
        <h3>Cadastro de Tabela de Preço</h3>
      </template>
      <template v-slot:body>
        <template v-if="stateReady">
          <form @submit.prevent="submitSave">
            <entity-selector-cliente
              v-if="amIAllowed('view', 'root-cliente')"
              :cliente="entity.cliente"
              :canEdit="!entity.cliente?.idCliente && !stateBlocked"
              @confirm-selection="confirmSelectionCliente"
              :required="true"
              :parentLoading="stateBlocked"
            />
            <fieldset :disabled="stateBlocked">
              <div class="p-formgrid p-grid">
                <div class="p-field p-fluid p-col-12 p-md-6">
                  <entity-selector-orgao-aet
                    :idOrgao="entity.idOrgao ?? undefined"
                    :required="true"
                    @after-set-orgao="afterSetOrgao"
                  />
                </div>

                <div class="p-field p-fluid p-col-12 p-md-6">
                  <input-with-label
                    label="Tipo de Solicitação"
                    :required="true"
                    v-slot="l"
                  >
                    <Dropdown
                      v-model="entity.tipoSolicitacao"
                      :options="tiposSolicitacao"
                      optionLabel="descricao"
                      optionValue="idTipoLicencaOrgao"
                      :showClear="true"
                      :id="l.id"
                      :required="l.required"
                      :placeholder="'&nbsp;'"
                    />
                  </input-with-label>
                </div>

                <div class="p-field p-fluid p-col-12 p-md-6">
                  <input-with-label
                    label="Tipo de Veículo"
                    subLabel="Deixe em branco para regra genérica"
                    v-slot="l"
                  >
                    <Dropdown
                      v-model="entity.tipoVeiculo"
                      :options="categoriasGabarito"
                      optionLabel="descricao"
                      optionValue="flag"
                      :showClear="true"
                      :id="l.id"
                      :required="l.required"
                      :placeholder="'&nbsp;'"
                    />
                  </input-with-label>
                </div>

                <div class="p-field p-fluid p-col-12 p-md-4">
                  <input-with-label
                    label="Preço (R$)"
                    :required="true"
                    v-slot="l"
                  >
                    <InputNumber
                      v-model="entity.preco"
                      :id="l.id"
                      :required="l.required"
                      mode="currency"
                      currency="BRL"
                      locale="pt-BR"
                      :minFractionDigits="2"
                    />
                  </input-with-label>
                </div>

                <div class="p-field p-fluid p-col-12 p-md-2">
                  <input-with-label
                    label="Ativo"
                    v-slot="l"
                    :floatLabel="false"
                  >
                    <InputSwitch v-model="entity.flAtivo" :id="l.id" />
                  </input-with-label>
                </div>

                <div class="p-col-12">
                  <Button
                    type="submit"
                    class="p-button-rounded"
                    label="Salvar"
                  />
                </div>
              </div>
            </fieldset>
          </form>
        </template>
      </template>
    </container>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  Ref,
  PropType
} from "vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { useToast } from "../../modules/message";
import {
  TabelaPrecoPayload,
  useTabelaPrecoService
} from "../../modules/business/orcamento/tabelaPrecoBusiness";
import { useTipoLicencaOrgaoService } from "../../modules/business/licenca/tipoLicencaOrgaoBusiness";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";
import EntitySelectorOrgaoAet from "../common/EntitySelectorOrgaoAet.vue";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";

const categoriasGabarito = [
  { flag: "CVC", descricao: "CVC (Combinação de Veículos de Carga)" },
  { flag: "GUINDASTE", descricao: "Guindaste" },
  { flag: "INDIVISIVEL", descricao: "Carga Indivisível" },
  {
    flag: "OUTROS",
    descricao: "Outros (Prancha, Piscina, Rollon-Rolloff, etc.)"
  }
];

interface TabelaPrecoCadastroState {
  entity: TabelaPrecoPayload & { cliente?: ClientePayload | null };
  stateReady: boolean;
  savedEntity: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<TabelaPrecoCadastroState>({
      entity: useTabelaPrecoService().safeTabelaPreco(),
      savedEntity: false,
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIAllowed(authority, permissionInfo);

    const stateLoading = computed(
      () => state.loadings.length !== 0 || props.parentLoading
    );

    const stateBlocked = computed(
      () =>
        !!(
          stateLoading.value ||
          (props.idTabelaPreco &&
            !amIAllowed("edit", state.entity.permissionInfo))
        )
    );

    const tiposSolicitacao = computed(() =>
      useTipoLicencaOrgaoService().getTiposLicencaOrgao()
    );

    const setEntity = (entity: TabelaPrecoPayload) => {
      state.entity = useTabelaPrecoService().safeTabelaPreco(entity);
    };

    const closeScreen = () => {
      emit(
        "after-register-screen-closed",
        state.savedEntity ? state.entity : undefined
      );
    };

    const confirmSelectionCliente = (c: ClientePayload) => {
      if (c) {
        state.entity.cliente = c;
      }
    };

    const afterSetOrgao = (idOrgao: number | null) => {
      state.entity.idOrgao = idOrgao;
    };

    const submitSave = () => {
      const idProps = props.idTabelaPreco ?? 0;
      const isInsert = !idProps;
      const {
        saveNewTabelaPreco,
        updateTabelaPreco,
        loading,
        data
      } = useTabelaPrecoService();
      state.loadings.push(loading);
      (isInsert
        ? saveNewTabelaPreco(state.entity)
        : updateTabelaPreco(idProps, state.entity)
      )
        .then(() => {
          setEntity(data.value);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.savedEntity = true;
          useToast().success("Tabela de preço salva com sucesso");
          if (isInsert) {
            emit("after-save-new-entity", data.value.idTabelaPreco);
          }
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    onMounted(async () => {
      if (props.idTabelaPreco) {
        const { getTabelaPreco, loading, data } = useTabelaPrecoService();
        state.loadings.push(loading);
        getTabelaPreco(props.idTabelaPreco)
          .then(() => {
            setEntity(data.value);
            state.loadings.splice(state.loadings.indexOf(loading), 1);
            state.stateReady = true;
          })
          .catch(() => {
            state.loadings.splice(state.loadings.indexOf(loading), 1);
            closeScreen();
          });
      } else {
        if (props.cliente) {
          state.entity.cliente = props.cliente;
        }
        state.stateReady = true;
      }
    });

    return {
      submitSave,
      closeScreen,
      amIAllowed,
      confirmSelectionCliente,
      afterSetOrgao,
      tiposSolicitacao,
      categoriasGabarito,
      stateLoading,
      stateBlocked,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorCliente,
    EntitySelectorOrgaoAet
  },
  props: {
    idTabelaPreco: Number,
    cliente: Object as PropType<ClientePayload>,
    parentLoading: Boolean
  }
});
</script>

<style scoped lang="scss"></style>
